import { CallDataSource, CallUser } from '@act/shared/data-transfer-objects';
import { CallStatus } from '@act/shared/definitions';
import {
  Box,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

export interface Data {
  from: CallUser;
  to: CallUser;
  timeStarted: number;
  voicemailFileId?: string;
  duration: number;
  status: CallStatus;
}

export function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  let sortParamA, sortParamB;

  if (orderBy === 'from') {
    sortParamA = (a as unknown as CallDataSource)?.originator?.phoneNumber;
    sortParamB = (b as unknown as CallDataSource)?.originator?.phoneNumber;
  }

  if (orderBy === 'to') {
    sortParamA = (a as unknown as CallDataSource)?.receiver?.phoneNumber;
    sortParamB = (b as unknown as CallDataSource)?.receiver?.phoneNumber;
  }

  if (orderBy === 'dateAndTime') {
    sortParamA = (a as unknown as CallDataSource)?.timeStarted;
    sortParamB = (b as unknown as CallDataSource)?.timeStarted;
  }

  if (orderBy === 'status') {
    sortParamA = (a as unknown as CallDataSource)?.status;
    sortParamB = (b as unknown as CallDataSource)?.status;
  }

  const valueA = sortParamA || a[orderBy];
  const valueB = sortParamB || b[orderBy];

  if (valueB < valueA) {
    return -1;
  }
  if (valueB > valueA) {
    return 1;
  }
  return 0;
}

export type Order = 'asc' | 'desc';

export function getComparator<Key extends keyof CallDataSource>(
  order: Order,
  orderBy: Key
): (a: CallDataSource, b: CallDataSource) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof CallDataSource;
  label: string;
  sortable: boolean;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'from',
    numeric: false,
    disablePadding: false,
    sortable: false, // TODO enable on backend, then set true
    label: 'From',
  },
  {
    id: 'to',
    numeric: false,
    disablePadding: false,
    sortable: false, // TODO enable on backend, then set true
    label: 'To',
  },
  {
    id: 'timeStarted',
    numeric: false,
    disablePadding: false,
    sortable: false, // TODO enable on backend, then set true
    label: 'Date and time',
  },
  {
    id: 'voicemailFileId',
    numeric: false,
    disablePadding: false,
    sortable: false,
    label: 'Voicemail',
  },
  {
    id: 'length',
    numeric: true,
    disablePadding: false,
    sortable: false,
    label: 'Duration',
  },
  {
    id: 'status',
    numeric: false,
    disablePadding: false,
    sortable: false, // TODO enable on backend, then set true
    label: 'Status',
  },
  {
    id: 'note',
    numeric: false,
    disablePadding: false,
    sortable: false,
    label: '',
  },
];

interface SortableCallsTableHeadersProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof CallDataSource
  ) => void;
  order: Order;
  orderBy: string;
}

const SortableCallsTableHeaders = (props: SortableCallsTableHeadersProps) => {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (property: keyof CallDataSource) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              disabled={!headCell.sortable}
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export default SortableCallsTableHeaders;
